import { MasterBarcodeEntry, MasterUPCData } from '../types';

export type MasterLabelTrailingTile =
  | {
      kind: 'loading';
      key: string;
      query: string;
    }
  | {
      kind: 'no-results';
      key: string;
      query: string;
    };

export function countMasterLabelBarcodeRows(upc: MasterUPCData): number {
  return (
    (upc.barcodeSummary?.primaryUpcs.length ?? 0) +
    (upc.barcodeSummary?.alternateUpcs.length ?? 0) +
    (upc.barcodeSummary?.casePacks.length ?? 0)
  );
}

export function countMasterLabelCrops(upc: MasterUPCData): number {
  return (
    upc.designs.reduce((total, design) => total + design.crops.length, 0) +
    (upc.pendingCrops?.length ?? 0) +
    upc.unassignedCrops.length +
    upc.cartonCrops.length +
    (upc.inactiveCrops?.length ?? 0)
  );
}

export function isDisplayableMasterLabelColumn(upc: MasterUPCData): boolean {
  if (isBarcodeOnlyMasterLabelColumn(upc)) {
    return countMasterLabelCrops(upc) > 0 || upc.designs.length > 0;
  }
  return countMasterLabelBarcodeRows(upc) > 0 || countMasterLabelCrops(upc) > 0;
}

function calculateGtinCheckDigit(value: string): string {
  const padded = value.padStart(13, '0');
  const total = [...padded].reduce(
    (sum, digit, index) => sum + Number(digit) * (index % 2 === 0 ? 3 : 1),
    0
  );
  return String((10 - (total % 10)) % 10);
}

function barcodeMatchKey(value: string): string {
  const trimmed = value.trim();
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return trimmed;
  if (digits.length >= 14) {
    const halfLength = digits.length / 2;
    return digits.length >= 16 && Number.isInteger(halfLength)
      && digits.slice(0, halfLength) === digits.slice(halfLength)
      ? digits.slice(0, halfLength)
      : digits;
  }

  const padded = digits.padStart(14, '0');
  const alreadyHasCheckDigit = /^\d+$/.test(trimmed)
    && calculateGtinCheckDigit(padded.slice(0, -1)) === padded.at(-1);
  return (alreadyHasCheckDigit ? digits : `${digits}${calculateGtinCheckDigit(digits)}`).padStart(14, '0');
}

function isItemNumberDerivedBarcode(barcode: string, itemNumber: string | null | undefined): boolean {
  const trimmedItemNumber = itemNumber?.trim();
  const trimmedBarcode = barcode.trim();
  if (!trimmedItemNumber || !trimmedBarcode) return false;
  if (
    trimmedBarcode.toLowerCase() === trimmedItemNumber.toLowerCase()
    || trimmedBarcode.toLowerCase() === `in${trimmedItemNumber}`.toLowerCase()
  ) {
    return true;
  }
  const barcodeKey = barcodeMatchKey(trimmedBarcode);
  return (
    barcodeKey === barcodeMatchKey(trimmedItemNumber)
    || barcodeKey === barcodeMatchKey(`IN${trimmedItemNumber}`)
  );
}

export function getNewDesignBarcodeCandidates(upc: MasterUPCData): MasterBarcodeEntry[] {
  if (isBarcodeOnlyMasterLabelColumn(upc)) {
    const normalizedBarcode = upc.lookupNormalizedBarcode?.trim();
    if (!normalizedBarcode) return [];
    const existingEntry = [
      ...(upc.barcodeSummary?.primaryUpcs ?? []),
      ...(upc.barcodeSummary?.alternateUpcs ?? []),
    ].find((entry) => entry.upc.trim() === normalizedBarcode);
    return existingEntry
      ? [existingEntry]
      : [{ upc: normalizedBarcode, provenance: ['Lookup'], storeDetails: [] }];
  }

  const byBarcode = new Map<string, MasterBarcodeEntry>();
  [
    ...(upc.barcodeSummary?.primaryUpcs ?? []),
    ...(upc.barcodeSummary?.alternateUpcs ?? []),
  ].forEach((entry) => {
    const normalizedBarcode = entry.upc.trim();
    const itemNumber = upc.actualItemNumber ?? upc.itemNumber;
    if (
      normalizedBarcode
      && !isItemNumberDerivedBarcode(normalizedBarcode, itemNumber)
      && !byBarcode.has(normalizedBarcode)
    ) {
      byBarcode.set(normalizedBarcode, entry);
    }
  });
  return [...byBarcode.values()];
}

export function isBarcodeOnlyMasterLabelColumn(upc: MasterUPCData): boolean {
  return upc.lookupKind === 'normalized_barcode' && !upc.actualItemNumber;
}
